import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Search, Plus, ArrowDownToLine, ArrowUpFromLine, CheckCircle2, AlertTriangle, CalendarClock, Factory } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { StoreBulkUpload } from '../components/StoreBulkUpload';
import { FeedPlantRegisterUpload } from '../components/FeedPlantRegisterUpload';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmtTime = (iso) => { if (!iso) return '—'; const d = new Date(iso); return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
const fmtQty = (n) => (n == null || isNaN(n)) ? '—' : (+n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtMoney = (n) => (n == null || isNaN(n)) ? '—' : '₹' + Math.round(n).toLocaleString('en-IN');
const fmtMoney2 = (n) => '₹' + (+n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().slice(0, 10);

/**
 * Feed Plant Division manual entry — mirror of StoresEntryPage but bound
 * to /api/feed-plant/* endpoints. Every Feed Plant item carries a
 * department (Electrical / Mechanical / ...), so the item picker surfaces
 * it and the "add new item" flow asks for it too. Backdate rules match
 * General Stores exactly: admin unrestricted, stores capped at 3 days back.
 */
const FeedPlantEntryPage = () => {
  const { isAdmin, isStores } = useAuth();
  const canBackdate = isAdmin || isStores;
  const minEntryDate = (() => {
    if (isAdmin) return undefined;
    if (isStores) {
      const d = new Date();
      d.setDate(d.getDate() - 3);
      return d.toISOString().slice(0, 10);
    }
    return todayStr();
  })();
  const [kind, setKind] = useState('issue');
  const [entryDate, setEntryDate] = useState(todayStr());
  const [itemQuery, setItemQuery] = useState('');
  const [itemOptions, setItemOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemUnit, setNewItemUnit] = useState('');
  const [newItemDept, setNewItemDept] = useState('');
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const searchTimer = useRef(null);
  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const loadActivity = useCallback(async () => {
    try {
      setLoadingActivity(true);
      const res = await axios.get(`${API}/api/feed-plant/transactions`, { withCredentials: true });
      setActivity(res.data.transactions || []);
    } catch (e) {} finally { setLoadingActivity(false); }
  }, []);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!itemQuery.trim() || selectedItem) { setItemOptions([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/feed-plant/items`, { params: { q: itemQuery }, withCredentials: true });
        setItemOptions(res.data.items || []);
        setShowDropdown(true);
      } catch (e) {}
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [itemQuery, selectedItem]);

  const pickItem = (item) => {
    setSelectedItem(item);
    setItemQuery(`${item.name} (${item.code})`);
    setUnitCost(item.rate != null ? String(item.rate) : '');
    setShowDropdown(false);
    setShowAddNew(false);
  };

  const clearItem = () => {
    setSelectedItem(null);
    setItemQuery('');
    setItemOptions([]);
    setShowAddNew(false);
  };

  const resetForm = () => {
    clearItem();
    setQty('');
    setUnitCost('');
    setNote('');
    setNewItemName('');
    setNewItemUnit('');
    setNewItemDept('');
  };

  const canSubmit = (selectedItem || (showAddNew && itemQuery.trim() && newItemName.trim()))
    && qty && +qty > 0
    && (kind === 'issue' || (unitCost && +unitCost >= 0));

  const isBackdated = canBackdate && entryDate !== todayStr();

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setFeedback(null);
    const item_code = selectedItem ? selectedItem.code : itemQuery.trim();
    try {
      const res = await axios.post(`${API}/api/feed-plant/transactions`, {
        item_code,
        kind,
        qty: +qty,
        unit_cost: kind === 'receipt' ? +unitCost : null,
        note: note.trim() || null,
        new_item_name: !selectedItem ? (newItemName.trim() || item_code) : null,
        new_item_unit: !selectedItem ? (newItemUnit.trim() || null) : null,
        new_item_department: !selectedItem ? (newItemDept.trim() || null) : null,
        entry_date: canBackdate ? entryDate : undefined,
      }, { withCredentials: true });
      const when = isBackdated ? ` for ${entryDate}` : '';
      const recomputeNote = res.data?.recomputed_dates?.length
        ? ` History recomputed through ${res.data.recomputed_dates[res.data.recomputed_dates.length - 1]}.`
        : '';
      const label = selectedItem ? selectedItem.name : (newItemName.trim() || item_code);
      setFeedback({ ok: true, message: `Logged ${kind === 'receipt' ? 'receipt' : 'issue'} of ${qty} for ${label}${when}.${recomputeNote}` });
      resetForm();
      loadActivity();
    } catch (e) {
      setFeedback({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="feed-plant-entry-page">
      <div>
        <h1 className="text-xl font-bold text-dash-ink flex items-center gap-2">
          <Factory className="w-5 h-5 text-dash-ice" /> Log Feed Plant Stock Entry
        </h1>
        <p className="text-sm text-dash-mut mt-1">Record a receipt or issue directly — no Excel file needed. The Feed Plant Division dashboard's balance updates as soon as you submit.</p>
      </div>

      <div className="bg-dash-panel border border-dash-line rounded-xl p-5 space-y-4">
        <div className="flex gap-1 bg-dash-panel2 border border-dash-line rounded-lg p-1 w-fit">
          <button onClick={() => setKind('issue')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition ${kind === 'issue' ? 'bg-dash-panel text-dash-ink font-semibold shadow-sm' : 'text-dash-mut hover:text-dash-ink'}`}
            data-testid="fp-kind-issue">
            <ArrowUpFromLine className="w-3.5 h-3.5" /> Issue
          </button>
          <button onClick={() => setKind('receipt')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition ${kind === 'receipt' ? 'bg-dash-panel text-dash-ink font-semibold shadow-sm' : 'text-dash-mut hover:text-dash-ink'}`}
            data-testid="fp-kind-receipt">
            <ArrowDownToLine className="w-3.5 h-3.5" /> Receipt
          </button>
        </div>

        {canBackdate && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Date</label>
            <div className="relative max-w-[180px]">
              <CalendarClock className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-dash-mut" />
              <input type="date" value={entryDate} max={todayStr()} min={minEntryDate}
                onChange={(e) => setEntryDate(e.target.value || todayStr())}
                className="w-full bg-dash-panel2 border border-dash-line rounded-lg pl-8 pr-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                data-testid="fp-entry-date-input" />
            </div>
            {isStores && !isAdmin && (
              <div className="mt-1.5 text-[11px] text-dash-mut">Stores users can backdate up to 3 days. Older entries need admin.</div>
            )}
            {isBackdated && (
              <div className="mt-1.5 text-[11px] text-dash-coral flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Backdating to {entryDate} — this recomputes history from that day forward.
              </div>
            )}
          </div>
        )}

        <div className="relative" ref={dropdownRef}>
          <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Item</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-dash-mut" />
            <input type="text" value={itemQuery}
              onChange={(e) => { setItemQuery(e.target.value); setSelectedItem(null); }}
              onFocus={() => itemOptions.length > 0 && setShowDropdown(true)}
              placeholder="Search item name or department..."
              className="w-full bg-dash-panel2 border border-dash-line rounded-lg pl-8 pr-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
              data-testid="fp-item-search-input" />
          </div>
          {showDropdown && itemOptions.length > 0 && !selectedItem && (
            <div className="absolute z-20 mt-1 w-full bg-dash-panel border border-dash-line rounded-lg shadow-xl max-h-56 overflow-auto">
              {itemOptions.map((it) => (
                <button key={it.code} onClick={() => pickItem(it)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-dash-panel2 border-b border-dash-line last:border-b-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-dash-ink font-semibold truncate">{it.name}</div>
                    <div className="font-mono text-dash-ice shrink-0">{it.rate != null ? `${fmtMoney2(it.rate)}/unit` : 'no rate'}</div>
                  </div>
                  <div className="text-dash-mut truncate">
                    <span className="font-mono">{it.code}</span> · Stock {fmtQty(it.stock)}{it.department ? ` · ${it.department}` : ''}{it.unit ? ` · ${it.unit}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
          {itemQuery.trim() && !selectedItem && itemOptions.length === 0 && (
            <div className="mt-2">
              {!showAddNew ? (
                <button onClick={() => setShowAddNew(true)} className="text-xs text-dash-ice inline-flex items-center gap-1 hover:underline" data-testid="fp-show-add-new-item">
                  <Plus className="w-3.5 h-3.5" /> "{itemQuery.trim()}" isn't in the list — add it as a new item
                </button>
              ) : (
                <div className="bg-dash-panel2 border border-dash-line rounded-lg p-3 space-y-2 mt-1">
                  <div className="text-[11px] text-dash-mut">New item code: <span className="font-mono text-dash-ink">{itemQuery.trim()}</span></div>
                  <input type="text" value={newItemName} onChange={(e) => setNewItemName(e.target.value)}
                    placeholder="Item name (required)"
                    className="w-full bg-dash-panel border border-dash-line rounded px-2.5 py-1.5 text-xs text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                    data-testid="fp-new-item-name-input" />
                  <input type="text" value={newItemDept} onChange={(e) => setNewItemDept(e.target.value)}
                    placeholder="Department (e.g. Electrical, Mechanical)"
                    className="w-full bg-dash-panel border border-dash-line rounded px-2.5 py-1.5 text-xs text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                    data-testid="fp-new-item-dept-input" />
                  <input type="text" value={newItemUnit} onChange={(e) => setNewItemUnit(e.target.value)}
                    placeholder="Unit (optional, e.g. Nos, kg, Ltr)"
                    className="w-full bg-dash-panel border border-dash-line rounded px-2.5 py-1.5 text-xs text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                    data-testid="fp-new-item-unit-input" />
                </div>
              )}
            </div>
          )}
          {selectedItem && (
            <div className="mt-1.5 flex items-center gap-2 text-[11px] text-dash-mut">
              <CheckCircle2 className="w-3.5 h-3.5 text-dash-green" />
              <span data-testid="fp-selected-item-info">
                <span className="font-mono">{selectedItem.code}</span> · Rate {selectedItem.rate != null ? fmtMoney2(selectedItem.rate) : '—'} · Stock {fmtQty(selectedItem.stock)}{selectedItem.department ? ` · ${selectedItem.department}` : ''}{selectedItem.unit ? ` · ${selectedItem.unit}` : ''}
              </span>
              <button onClick={clearItem} className="text-dash-ice hover:underline ml-1">change</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Quantity</label>
            <input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0"
              className="w-full bg-dash-panel2 border border-dash-line rounded-lg px-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
              data-testid="fp-qty-input" />
          </div>
          {kind === 'receipt' && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Unit cost (₹)</label>
              <input type="number" min="0" step="any" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} placeholder="0.00"
                className="w-full bg-dash-panel2 border border-dash-line rounded-lg px-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                data-testid="fp-unit-cost-input" />
            </div>
          )}
          {kind === 'issue' && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Rate (₹)</label>
              <div className="w-full bg-dash-panel2/60 border border-dash-line rounded-lg px-3 py-2 text-sm text-dash-mut" data-testid="fp-issue-rate-display">
                {selectedItem?.rate != null ? fmtMoney2(selectedItem.rate) : '—'} <span className="text-[10px]">· FIFO</span>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Note (optional)</label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={kind === 'issue' ? 'e.g. issued to Feed Line 2 for planned maintenance' : 'e.g. PO #4821, vendor delivery'}
            className="w-full bg-dash-panel2 border border-dash-line rounded-lg px-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
            data-testid="fp-note-input" />
        </div>

        {feedback && (
          <div className={`rounded-lg px-3 py-2 text-xs border flex items-start gap-1.5 ${feedback.ok ? 'border-dash-green/30 bg-[rgba(107,203,119,.08)] text-dash-ink' : 'border-dash-coral/30 bg-[rgba(255,107,138,.08)] text-dash-coral'}`}>
            {feedback.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            {feedback.message}
          </div>
        )}

        <button onClick={submit} disabled={!canSubmit || submitting}
          className="w-full bg-dash-ice text-white px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="fp-submit-transaction">
          {submitting ? 'Logging…' : `Log ${kind === 'receipt' ? 'Receipt' : 'Issue'}`}
        </button>
      </div>

      <FeedPlantRegisterUpload onUploaded={loadActivity} />

      <StoreBulkUpload apiBase="feed-plant" filename="Feed_Plant_Entries_Template.xlsx" testidPrefix="fp" onUploaded={loadActivity} />

      <div className="bg-dash-panel border border-dash-line rounded-xl p-4">
        <h3 className="text-sm font-semibold text-dash-ink mb-3">Today's activity</h3>
        {loadingActivity ? (
          <div className="text-xs text-dash-mut italic py-4 text-center">Loading…</div>
        ) : activity.length === 0 ? (
          <div className="text-xs text-dash-mut italic py-4 text-center">Nothing logged yet today.</div>
        ) : (
          <div className="space-y-1.5 max-h-96 overflow-auto">
            {activity.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between gap-3 bg-dash-panel2 border border-dash-line rounded-lg px-3 py-2 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  {tx.kind === 'receipt' ? <ArrowDownToLine className="w-3.5 h-3.5 text-dash-green shrink-0" /> : <ArrowUpFromLine className="w-3.5 h-3.5 text-dash-coral shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-dash-ink truncate">{tx.item_name || tx.item_code} <span className="font-mono text-dash-mut">({tx.item_code})</span>{tx.department ? ` · ${tx.department}` : ''}</div>
                    {tx.note && <div className="text-dash-mut truncate">{tx.note}</div>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-dash-ink font-mono">{fmtQty(tx.qty)}{tx.unit ? ` ${tx.unit}` : ''}</div>
                  <div className="text-dash-mut">{tx.kind === 'receipt' && tx.unit_cost != null ? fmtMoney(tx.unit_cost * tx.qty) + ' · ' : ''}{fmtTime(tx.created_at)}{tx.created_by_name ? ` · ${tx.created_by_name}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedPlantEntryPage;
